import { Alert, Modal, Switch, message } from 'antd'
import { ModalAction } from 'common/components/Modal'
import { Table } from 'common/components/Table/Table'
import { useTimezone } from 'common/hooks/useTimezone'
import { useSites } from 'common/hooks/useSites'
import { DATE_FORMAT } from 'common/constants/dateFormatConstants'
import { DateService } from 'common/services/dateService'
import { Button } from 'common/components/Button/Button'
import { BUTTON_CONSTANTS, BUTTON_MODIFIER } from 'common/constants/buttonConstants'
import type { IPatientConsultationStaff } from 'features/Home/interfaces/IInfoPatient'
import {
  useCreateAvailabilitySeriesMutation,
  useDeleteAvailabilitySeriesMutation,
  useDiscardAvailabilitySeriesMutation,
  useFetchAvailabilityKindsQuery,
  useFetchAvailabilitySeriesQuery,
  useRetryAvailabilitySeriesMutation,
  useUpdateAvailabilitySeriesMutation,
} from 'features/Schedule/api/availabilitySeries.api'
import type { AvailabilitySeries } from 'features/Schedule/api/availabilitySeries.types'
import { useEffect, useMemo, useRef, useState } from 'react'
import { getDoctorScheduleColumns } from './columns'
import { DoctorScheduleForm } from './DoctorScheduleForm'
import {
  availabilityErrorText,
  blankDraft,
  isPastSeries,
  rowLock,
  rowToDraft,
  draftToWriteBody,
  type BlockDraft,
} from './availabilitySeries.utils'
import styles from './doctorSchedule.module.scss'

type DoctorScheduleModalProps = {
  staff: IPatientConsultationStaff | null
  providers: IPatientConsultationStaff[]
  siteId?: string
  date: string | null
  onClose: () => void
}

const RELOAD_CODES = new Set(['stale_row', 'not_found', 'changed_meanwhile', 'not_failed'])

export const DoctorScheduleModal = ({ staff, providers, siteId, date, onClose }: DoctorScheduleModalProps) => {
  const open = staff != null && !!siteId
  const { timeZone } = useTimezone()
  const today = DateService.getCurrentTimeInTimezone(timeZone).format(DATE_FORMAT.PATIENT_TRACKER_DATE)
  const [showPast, setShowPast] = useState(false)
  const [draft, setDraft] = useState<BlockDraft | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [formError, setFormError] = useState<string | null>(null)
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null)
  const [polling, setPolling] = useState(0)
  const watched = useRef<Map<number, string | null>>(new Map())

  const seriesQuery = useFetchAvailabilitySeriesQuery(
    { siteId: siteId ?? '', userId: staff?.id ?? 0 },
    { skip: !open, pollingInterval: polling },
  )
  const kindsQuery = useFetchAvailabilityKindsQuery(
    { siteId: siteId ?? '' },
    { skip: !open },
  )
  const [createSeries, createState] = useCreateAvailabilitySeriesMutation()
  const [updateSeries, updateState] = useUpdateAvailabilitySeriesMutation()
  const [deleteSeries, deleteState] = useDeleteAvailabilitySeriesMutation()
  const [retrySeries, retryState] = useRetryAvailabilitySeriesMutation()
  const [discardSeries, discardState] = useDiscardAvailabilitySeriesMutation()

  const rows = seriesQuery.currentData?.rows ?? []
  const kinds = kindsQuery.data?.kinds ?? []
  const visitKinds = kindsQuery.data?.visit_kinds ?? []
  const saving = createState.isLoading || updateState.isLoading || deleteState.isLoading
  const { selectedSite } = useSites()
  const siteName = rows[0]?.site.name ?? selectedSite?.name ?? ''
  const providerOptions = useMemo(() => {
    if (!staff) return providers
    if (providers.some((provider) => provider.id === staff.id)) return providers
    return [staff, ...providers]
  }, [providers, staff])

  useEffect(() => {
    setDraft(null)
    setShowPast(false)
    setFieldErrors({})
    setFormError(null)
    setPendingDeleteId(null)
    watched.current.clear()
  }, [staff?.id])

  useEffect(() => {
    const hasPending = rows.some((row) => row.sync_status === 'pending')
    setPolling(hasPending ? 5000 : 0)
    rows.forEach((row) => {
      if (!watched.current.has(row.id)) return
      const seen = watched.current.get(row.id)
      if (row.changed_by === 'openemr' && row.sync_status === 'synced' && row.changed_at !== seen) {
        message.info('This block was changed in the EMR at the same time. The EMR version is kept.')
        watched.current.delete(row.id)
      } else if (row.sync_status === 'synced' && row.changed_by === 'residen') {
        watched.current.delete(row.id)
      }
    })
  }, [rows])

  const visibleRows = useMemo(
    () =>
      rows
        .filter((row) => showPast || !isPastSeries(row, today))
        .slice()
        .sort((a, b) => a.start_date.localeCompare(b.start_date) || a.start_time.localeCompare(b.start_time)),
    [rows, showPast, today],
  )
  const pastCount = rows.filter((row) => isPastSeries(row, today)).length
  const pastLocked =
    draft?.id != null && rows.some((row) => row.id === draft.id && isPastSeries(row, today))

  const remember = (saved: AvailabilitySeries[]) => {
    saved.forEach((row) => watched.current.set(row.id, row.changed_at))
  }

  const showFailure = async (error: unknown) => {
    const parsed = availabilityErrorText(error)
    setFormError(parsed.message)
    setFieldErrors(parsed.fields)
    if (parsed.code && RELOAD_CODES.has(parsed.code)) {
      await seriesQuery.refetch()
      if (parsed.code === 'not_found') setDraft(null)
    }
  }

  const handleSave = async (values: BlockDraft) => {
    if (!siteId) return
    const editing = rows.find((row) => row.id === values.id)
    if (editing && isPastSeries(editing, today)) return
    const written = draftToWriteBody(values)
    if (written.ok === false) {
      setFieldErrors(written.fields)
      setFormError(null)
      return
    }
    setFieldErrors({})
    setFormError(null)
    try {
      const result = values.id
        ? await updateSeries({ siteId, id: values.id, body: written.body }).unwrap()
        : await createSeries({ siteId, body: written.body }).unwrap()
      remember(result.rows)
      setDraft(null)
    } catch (error) {
      await showFailure(error)
    }
  }

  const handleDelete = async (id: number) => {
    if (!siteId) return
    setFormError(null)
    try {
      const result = await deleteSeries({ siteId, id }).unwrap()
      remember(result.rows)
      setDraft((current) => (current?.id === id ? null : current))
    } catch (error) {
      await showFailure(error)
    }
  }

  const openDraft = (next: BlockDraft) => {
    setFieldErrors({})
    setFormError(null)
    setDraft(next)
  }

  const columns = getDoctorScheduleColumns({
    kinds,
    rows,
    retrying: retryState.isLoading,
    discarding: discardState.isLoading,
    deletingId: deleteState.isLoading ? deleteState.originalArgs?.id ?? null : null,
    onRetry: (id) => {
      if (!siteId) return
      retrySeries({ siteId, id })
        .unwrap()
        .then((result) => remember(result.rows))
        .catch(showFailure)
    },
    onDiscard: (id) => {
      if (!siteId) return
      discardSeries({ siteId, id })
        .unwrap()
        .then((result) => remember(result.rows))
        .catch(showFailure)
    },
    onDelete: setPendingDeleteId,
  })

  const listError = seriesQuery.error ?? kindsQuery.error
  const listMessage = listError ? availabilityErrorText(listError).message : null

  const pendingRow = rows.find((row) => row.id === pendingDeleteId)
  const pendingName = pendingRow?.title || pendingRow?.kind.name || 'this block'

  return (
    <>
    <Modal
      open={staff != null}
      footer={null}
      destroyOnClose
      wrapClassName={styles.root}
      onCancel={onClose}
      title={null}>
      <div className={styles.header}>
        <div>
          <div className={styles.kicker}>{draft ? (draft.id ? 'Edit block' : 'New block') : 'Scheduling'}</div>
          <div className={styles.title}>{staff?.name}</div>
          {siteName ? <div className={styles.site}>{siteName}</div> : null}
        </div>
        <div className={styles.spacer} />
        {!draft ? (
          <Button
            modifier={BUTTON_MODIFIER.PRIMARY}
            disabled={!kinds.length}
            onClick={() => {
              if (!staff) return
              const kind = kinds[0]
              openDraft(blankDraft(staff.id, date || today, kind?.id ?? null, kind?.name ?? ''))
            }}>
            New block
          </Button>
        ) : null}
      </div>

      {formError ? (
        <Alert className={styles.banner} type='error' showIcon message={formError} />
      ) : null}
      {listMessage && !draft ? (
        <Alert className={styles.banner} type='error' showIcon message={listMessage} />
      ) : null}

      {!draft ? (
        <>
          <div className={styles.intro}>
            <div className={styles.introCopy}>
              <div className={styles.introTitle}>When they are here</div>
              <div className={styles.introHint}>
                Click a row to change it. Deleting removes the whole block and every repeat of it.
              </div>
            </div>
            <div className={styles.pastToggle}>
              <span>Show past ({pastCount})</span>
              <Switch checked={showPast} onChange={(checked) => setShowPast(checked)} />
            </div>
          </div>
          <Table
            itemKey='id'
            columns={columns}
            dataSource={visibleRows}
            isLoading={seriesQuery.isLoading || seriesQuery.isFetching && seriesQuery.currentData == null}
            pagination={false}
            haveActions={false}
            scroll={{ y: 360 }}
            onClickRow={(row: AvailabilitySeries) => {
              if (rowLock(row, rows) !== 'open') return
              openDraft(rowToDraft(row))
            }}
            rowClassName={(row: AvailabilitySeries) => (isPastSeries(row, today) ? styles.past : '')}
          />
        </>
      ) : (
        <DoctorScheduleForm
          draft={draft}
          kinds={kinds}
          visitKinds={visitKinds}
          providers={providerOptions}
          siteName={siteName}
          saving={saving}
          fieldErrors={fieldErrors}
          onClearFieldErrors={() => setFieldErrors({})}
          onSave={handleSave}
          onCancel={() => setDraft(null)}
          onDelete={() => draft.id != null && setPendingDeleteId(draft.id)}
          locked={pastLocked}
        />
      )}
    </Modal>
    <ModalAction
      zIndex={2000}
      rootClassName={styles.confirmRoot}
      isOpen={pendingDeleteId != null}
      title='Delete block'
      confirmText={BUTTON_CONSTANTS.DELETE}
      cancelText={BUTTON_CONSTANTS.CANCEL}
      description={`Are you sure you want to delete ${pendingName}? Every repeat of it is removed too.`}
      onClose={() => setPendingDeleteId(null)}
      onConfirm={() => {
        if (pendingDeleteId == null) return
        const id = pendingDeleteId
        setPendingDeleteId(null)
        handleDelete(id)
      }}
    />
    </>
  )
}
